/**
 * CatalogPicker — a typeahead combobox over an Oxbo-M catalog (§22 / §23). It
 * is a READ-ONLY consumer: it reads `GET /api/m/catalogs/{key}` via mApi,
 * displays `label` + `code`, and stores the selected `code`. It never creates,
 * edits, or deletes catalog values — catalogs are owned by Oxbo-M.
 *
 * The value list + popover are PORTALED (via <Popover>) so they escape modal +
 * table overflow clipping. Typeahead is client-side for small catalogs; large
 * ones (cost_accounts ~2,615) pass a server `q=` (wired via `serverSearch`).
 *
 * Admin ↗ (§22c, hard requirement): the catalog's admin link is rendered ONLY
 * when the response carries an `admin` block AND the user holds its capability.
 * For non-admins the server omits `admin` entirely, so the URL never reaches
 * the DOM — CSS-hiding would leak it via devtools, so we rely on omission +
 * the capability check, never on hiding.
 */
import { useMemo, useRef, useState } from "react";
import { useGetCatalogQuery } from "@api/mApi";
import type { CatalogValue } from "@api/types";
import { Popover } from "@components/input/Popover";
import { useCapabilities } from "./useCapabilities";

export interface CatalogPickerProps {
  /** Catalog key (grades, connections, cost_accounts, activity_codes, …). */
  catalogKey: string;
  /** Selected value code, or null. */
  value: string | null;
  /** Selection callback — receives the code + the full value (for attrs). */
  onChange: (code: string | null, value: CatalogValue | null) => void;
  /** Placeholder when nothing is selected. */
  placeholder?: string;
  /** Effective-date filter (report date) — offer only values valid as-of. */
  asOf?: string;
  /** Scope filter (region/tenant/event). */
  scope?: string;
  /**
   * Push typeahead to the server (`q=`) instead of filtering the loaded list —
   * for catalogs too large to ship whole. Default false (client-side).
   */
  serverSearch?: boolean;
  /** Compact styling for use inside an inline grid cell. */
  compact?: boolean;
  /** Report closed → disable selection. */
  readOnly?: boolean;
  /** aria-label. */
  ariaLabel?: string;
}

export function CatalogPicker({
  catalogKey,
  value,
  onChange,
  placeholder = "Select…",
  asOf,
  scope,
  serverSearch = false,
  compact = false,
  readOnly = false,
  ariaLabel,
}: CatalogPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { can } = useCapabilities();

  // Read the catalog. For server search, pass q through; otherwise load once.
  const { data, isFetching } = useGetCatalogQuery({
    key: catalogKey,
    asOf,
    scope,
    ...(serverSearch && query ? { q: query } : {}),
  });

  const values = useMemo(() => data?.values ?? [], [data]);

  // Client-side typeahead over label + code + aliases (skipped when server does it).
  const filtered = useMemo(() => {
    if (serverSearch) return values;
    const q = query.trim().toLowerCase();
    if (!q) return values;
    return values.filter(
      (v) =>
        v.label.toLowerCase().includes(q) ||
        v.code.toLowerCase().includes(q) ||
        (v.aliases ?? []).some((a) => a.toLowerCase().includes(q)),
    );
  }, [values, query, serverSearch]);

  const selected = values.find((v) => v.code === value) ?? null;

  // Admin ↗ only when the payload carried `admin` AND the user has the cap.
  const admin = data?.admin;
  const showAdminLink = !!admin && can(admin.capability);

  const select = (v: CatalogValue) => {
    onChange(v.code, v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="picker">
      <button
        ref={anchorRef}
        type="button"
        className={`cellpick${selected ? " picked" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? placeholder}
        disabled={readOnly}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <>
            <span className="code">{selected.code}</span>
            <span className="lbl">{selected.label}</span>
          </>
        ) : (
          <span className="lbl ph">{placeholder}</span>
        )}
        <span className="ic ic-chevron caret" aria-hidden="true" style={{ transform: "rotate(90deg)" }} />
      </button>

      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        className="picker-pop"
        role="listbox"
        aria-label={data?.label ?? catalogKey}
        minWidth={compact ? 240 : undefined}
      >
        <label className="psearch">
          <span className="ic ic-search" aria-hidden="true" />
          <input
            type="search"
            autoFocus
            placeholder="Type code or name…"
            aria-label="Search catalog"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        <div className="src">
          <span className="mk" aria-hidden="true" />
          Oxbo-M · {data?.label ?? catalogKey}
          {showAdminLink ? (
            <a
              className="manage-link"
              href={admin.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Manage in Oxbo-M admin"
              aria-label="Manage this catalog in Oxbo-M admin"
            >
              &nearr;
            </a>
          ) : null}
        </div>

        {isFetching && filtered.length === 0 ? (
          <div className="noresult">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="noresult">No matches</div>
        ) : (
          filtered.map((v) => (
            <button
              key={v.code}
              type="button"
              role="option"
              aria-selected={v.code === value}
              className={`opt${v.code === value ? " active" : ""}`}
              onClick={() => select(v)}
            >
              <span className="code">{v.code}</span>
              {v.label}
            </button>
          ))
        )}
      </Popover>
    </div>
  );
}
